import {
  MOBILE_OS,
  SUPPORTED_BROWSERS,
  APPLE_APP_LINK_PATHS,
  ExperimentCohortType,
  EXPERIMENT_COHORTS
} from '@strava/constants/src/appConstants';
import { isString } from 'lodash-es';
import camelCase from 'lodash-es/camelCase';
import snakeCase from 'lodash-es/snakeCase';
import isObject from 'lodash-es/isObject';
import {
  CamelCasedProperties,
  CamelCasedPropertiesDeep,
  SnakeCasedPropertiesDeep
} from 'type-fest';
import { devLog } from './dev.utils';

export const generateRandomId = () => Math.random().toString(36).substring(2);

// Capitalizes all the words and replaces some characters in the string to create a nicer looking title.
// eg. 'man from the boondocks' => 'Man From The Boondocks'
export const titleize = (sentence: string) => {
  return sentence
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const displayName = (
  { firstName = '', lastName = '' },
  options = { forceAnonymize: false, maxLength: 100 }
) => {
  const { maxLength, forceAnonymize } = options;
  const fullName = `${firstName} ${lastName}`;

  if (forceAnonymize || fullName.length > maxLength) {
    if (firstName.length > maxLength - 3) {
      return `${titleize(firstName.substring(0, maxLength - 4))}... ${lastName
        .charAt(0)
        .toUpperCase()}.`;
    }
    return `${titleize(firstName)} ${lastName.charAt(0).toUpperCase()}.`;
  }
  return fullName.trim();
};

/**
 * Checks to see if device is mobile
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {boolean} - true if the device is mobile
 */
export const isMobile = (
  userAgent = typeof window === 'undefined'
    ? null
    : window?.navigator?.userAgent
) => {
  if (isString(userAgent)) {
    // ref - https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    return userAgent.toLowerCase().includes('mobi');
  }
  return false;
};

/**
 * Determines the OS for mobile devices
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {(string|null)} - one of ['android', 'ipad', 'iphone'] or null for unknown
 */
export const getMobileOS = (
  userAgent = typeof window === 'undefined'
    ? null
    : window?.navigator?.userAgent
) => {
  if (isString(userAgent)) {
    if (userAgent.includes('Android')) {
      return MOBILE_OS.android;
    }
    if (userAgent.includes('iPad')) {
      return MOBILE_OS.iPad;
    }
    if (userAgent.includes('iPhone')) {
      return MOBILE_OS.iPhone;
    }
  }
  return null;
};

/**
 * Determines, in a limited fashion, the browser according to the user agent.
 * NOTE: UAs are notoriously difficult to parse. This is meant as a bare-bones, noncomprehensive parser,
 * based on https://www.whatismybrowser.com/guides/the-latest-user-agent/.
 *
 * The order of conditionals is important, bc some Chrome & Firefox UAs include 'Safari', too.
 * Some Edge UAs may be recognized as Chrome for now, since they can include 'Chrome'. We don't
 * officially support Edge (yet?).
 *
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {(string|null)} - one of ['chrome', 'firefox', 'safari'] or null for unknown/unsupported browser
 */
export const getBrowser = (userAgent = window?.navigator?.userAgent) => {
  if (isString(userAgent)) {
    if (userAgent.includes('Chrome') || userAgent.includes('CriOS')) {
      return SUPPORTED_BROWSERS.chrome;
    }
    if (userAgent.includes('Firefox') || userAgent.includes('FxiOS'))
      return SUPPORTED_BROWSERS.firefox;
    if (userAgent.includes('Safari')) {
      return SUPPORTED_BROWSERS.safari;
    }
  }

  return null;
};

/**
 * Universal links are determined by the paths described in `apple-app-site-association` in active.
 * @param {string} pathname - window.location.pathname or nextjs equivalent
 * @returns {boolean} - whether the pathname string matches
 */
export const isUniversalLinkPage = (pathname: string) => {
  if (isString(pathname)) {
    return APPLE_APP_LINK_PATHS.some((path: string) => {
      const pathWithoutAsterisk = path.replace('/*', '');
      return pathname.startsWith(pathWithoutAsterisk);
    });
  }
  return false;
};

// eg. given,
//  "urls": {
//     "100": "https://photo_url_1",
//     "1800": "https://photo_url_2"
// }
// returns https://photo_url_2
export const getPhotoWithMaxDimension = (
  photoUrlHash: Record<string, string | null | undefined>
) => {
  if (!isObject(photoUrlHash)) {
    return '';
  }
  const key = Object.keys(photoUrlHash).reduce((a, b) => (a > b ? a : b));
  return photoUrlHash[key];
};

/**
 * Immutably merges a payload into an item in an array of objects
 * @param {array} array - An array of objects
 * @param {string|number} id - The unique id of the item in the array to update
 * @param {object} payload - the value to merge into the matched item
 * @param {object} options
 * @param {string} [options.idName=id] - the key of the unique identifier
 * @param {bool} [options.upsert=false] - if true, will insert the payload into the array as a new item if no matching item is found
 * @return {array} - A copy of the original array with the matching item updated
 */
export function arrayUpdateItemById<T>(
  array: T[],
  id: string | number,
  payload: Partial<T>,
  { idName, upsert }: { idName?: keyof T; upsert?: boolean } = {}
): T[] {
  let itemFound = false;
  const property = idName || ('id' as keyof T);
  const mappedArray = array.map((item) => {
    itemFound = itemFound || item[property] === id;
    return item[property] === id ? { ...item, ...payload } : item;
  });

  return itemFound || !upsert ? mappedArray : ([...array, payload] as T[]);
}

/**
 * Immutably removes an item from an array by index
 * @param {array} array
 * @param {number} index - the index of the item to remove
 * @return {array} - A copy of the original array with the matching item removed
 */
export function arrayRemoveByIndex<T>(array: T[], index: number): T[] {
  return index === -1
    ? array
    : [...array.slice(0, index), ...array.slice(index + 1)];
}

/**
 * Immutably removes an item from an array of objects
 * @param {array} array - An array of objects
 * @param {string|number} id - The unique id of the item in the array to remove
 * @param {string} [idName=id] - the key of the unique identifier
 * @return {array} - A copy of the original array with the matching item removed
 */

export function arrayRemoveItemById<T>(
  array: T[],
  propertyValue: string | number,
  idName = 'id'
): T[] {
  const index = array.findIndex(
    (item) => item[idName as keyof T] === propertyValue
  );
  return arrayRemoveByIndex(array, index);
}

export const capitalizeFirstLetter = (string: string) =>
  `${string.charAt(0).toUpperCase()}${string.slice(1)}`;

/**
 * @description Converts Object keys from string of any case to camelCase.
 * Handles nested objects and arrays.
 * */
export function convertKeysToCamel<T>(
  object: T,
  isShallow: true
): CamelCasedProperties<T>;
export function convertKeysToCamel<T>(
  object: T,
  isShallow?: false
): CamelCasedPropertiesDeep<T>;
export function convertKeysToCamel<T>(object: T, isShallow?: boolean) {
  if (Array.isArray(object)) {
    return (object as unknown[]).map((item) =>
      convertKeysToCamel(item)
    ) as typeof isShallow extends true
      ? CamelCasedProperties<T>
      : CamelCasedPropertiesDeep<T>;
  }

  if (isObject(object)) {
    return Object.keys(object).reduce((o, k) => {
      const key = camelCase(k);
      const value = object[k as keyof typeof object] as CamelCasedProperties<T>;

      if (!isShallow && isObject(value)) {
        return {
          ...o,
          [key]: convertKeysToCamel(value)
        };
      }

      if (!isShallow && Array.isArray(value)) {
        return {
          ...o,
          [key]: (value as unknown[]).map((item) => convertKeysToCamel(item))
        };
      }

      return {
        ...o,
        [key]: value
      };
    }, {} as CamelCasedProperties<T>);
  }

  return object as typeof isShallow extends true
    ? CamelCasedProperties<T>
    : CamelCasedPropertiesDeep<T>;
}

/**
 * @description Converts Object keys from string of any case to snakeCase.
 * Handles nested objects and arrays.
 * */
export function convertKeysToSnake<T>(object: T): SnakeCasedPropertiesDeep<T> {
  if (Array.isArray(object)) {
    return (object as unknown[]).map((item) =>
      convertKeysToSnake(item)
    ) as SnakeCasedPropertiesDeep<T>;
  }

  if (isObject(object)) {
    return Object.keys(object).reduce((o, k) => {
      const key = snakeCase(k);
      const value = object[
        k as keyof typeof object
      ] as SnakeCasedPropertiesDeep<T>;

      if (isObject(value)) {
        return {
          ...o,
          [key]: convertKeysToSnake(value)
        };
      }

      if (Array.isArray(value)) {
        return {
          ...o,
          [key]: (value as unknown[]).map((item) => convertKeysToSnake(item))
        };
      }

      return {
        ...o,
        [key]: value
      };
    }, {} as SnakeCasedPropertiesDeep<T>);
  }

  return object as SnakeCasedPropertiesDeep<T>;
}

/**
 * Checks to see if localStorage is available
 *
 * @return {boolean} - true if localStorage can be used
 */
export const isLocalStorageAvailable = () => {
  const test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Checks to see if sessionStorage is available
 *
 * @return {boolean} - true if sessionStorage can be used
 */
export const isSessionStorageAvailable = () => {
  const test = 'test';
  try {
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Appends querystring params to a URL
 *
 * @param {string} originalUrl - The original URL to append params to
 * @param {object} params - An object of key value parameters to append to URL
 * @return {string} - URL with params appended
 */
export const addParamsToURL = (originalUrl: string, params: object) => {
  const url = new URL(originalUrl);

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  return url.toString();
};

/**
 * Get querystring param from URL
 *
 * @param {string} url - The full url
 * @param {string} param - A param to get value for
 * @return {string} - Param value
 */
export const getParamFromURL = (url: string, param: string) => {
  const [, queryParams] = url?.split('?') || [];
  const params = new URLSearchParams(queryParams);
  return params.get(param);
};

/**
 * Some of our logged-out pages have a full-screen image background.
 * This util helps with setting the background image on the `<body>` element, and
 * just requires passing in the image to be used.
 * @param {string} background
 */
export const setFullScreenBackgroundImage = (background: string) => {
  // apply background image directly to `<body>`
  const body = document.querySelector('body');
  if (body) {
    body.style.setProperty('background-image', `url(${background})`);
    body.style.setProperty('background-size', 'cover');
    body.style.setProperty('background-position', 'center');
  }
};

/**
 * Uses the host to determine if a page is being loaded in staging or localhost.
 *
 * This util function is helpful for apps that run on nextJS which has node process
 * defined as production in both staging and production.
 *
 * @param host - host name
 * @returns {boolean} - true if app is running in staging or local or webapp
 */
export const isStagingOrLocal = (host: string) =>
  ['staging', 'localhost', 'webapp'].some((_host) => host.includes(_host));

/**
 * Adds an id property to each object in an array. Uses the object's index as the value for the id
 * @param {array} objects - An array of objects
 * @return {array} - A copy of the original array with id property added to each object
 */
export const arrayAddIndexAsId = (objects: Array<object>) =>
  objects.map((object, index) => ({ ...object, id: index }));

/**
 * Uses the host to determine if the protocol should be http or https
 *
 * @param host - host name
 * @returns {string} - Protocol (http / https)
 */
export const getProtocolForHost = (host: string) => {
  return host.includes('localhost') ? 'http' : 'https';
};

/**
 * Constructs the site root URL
 *
 * @param host - host name
 * @returns {string} - Root URL with protocol
 */
export const getRootUrl = (host: string) => {
  return `${getProtocolForHost(host)}://${host}`;
};

/**
 * Constructs the athlete URL for pro and non-pro athletes, i.e.
 * https://www.strava.com/athletes/[athlete_id] or
 * https://www.strava.com/pros/[athlete_id]
 *
 * @param isPro - boolean indicating if the athlete is a pro or not
 * @param athleteId - athlete unique identifier
 * @param host - host name
 * @returns {string} - Root URL with protocol
 */
export const getAthleteUrl = (
  isPro: boolean,
  athleteId: number,
  host: string
) => {
  return `${getProtocolForHost(host)}://${host}/${
    isPro ? 'pros' : 'athletes'
  }/${athleteId.toString()}`;
};

/**
 * Creates a mock experiment object.
 * @param {string} url - The URL to extract the variant from.
 * @param {string} name - The name of the experiment.
 * @param {ExperimentCohortType} [cohort=EXPERIMENT_COHORTS.control] - The cohort of the experiment.
 * @returns {Object} The mock experiment object.
 */
export const mockExperiment = (
  url: string,
  name: string,
  cohort: ExperimentCohortType = EXPERIMENT_COHORTS.control
) => {
  const queryVariant = getParamFromURL(url, 'variant');
  let _cohort = cohort;

  switch (queryVariant) {
    case 'control':
      _cohort = EXPERIMENT_COHORTS.control;
      break;
    case 'a':
      _cohort = EXPERIMENT_COHORTS.variantA;
      break;
    case 'b':
      _cohort = EXPERIMENT_COHORTS.variantB;
      break;
    case 'c':
      _cohort = EXPERIMENT_COHORTS.variantC;
      break;
    case 'd':
      _cohort = EXPERIMENT_COHORTS.variantD;
      break;
    default:
      break;
  }
  return {
    name,
    cohort: _cohort,
    forced: true
  };
};

/**
 * Checks whether url goes to strava or a strava subdomain on prod, or
 * localhost or webapp on localhost/testing.
 * See `application_controller#valid_redirect_param` in active.
 * @param {string} url - the URL to check (must be an absolute URL)
 * @param {string} currentHost - the current host to check against
 * @returns {boolean} - true if url has a valid host for a strava redirect,
 * false otherwise
 */
export const isValidStravaUrlForCurrentHost = (
  url: string,
  currentHost: string
) => {
  try {
    const { host } = new URL(url);
    // always accept .strava.com
    if (/\.strava\.com/.test(host)) {
      return true;
    }
    // only accept localhost & webapp on localhost or webapp
    if (/localhost/.test(host) || /webapp/.test(host)) {
      return /localhost/.test(currentHost) || /webapp/.test(currentHost);
    }
    return false;
  } catch (error) {
    devLog(String(error));
    return false;
  }
};
